/** Merge admin overrides into static locale content without breaking list items. */

export function deepMerge(base, override) {
  if (override === null || override === undefined) return base;
  if (base === null || base === undefined) return override;

  if (Array.isArray(override)) {
    if (!Array.isArray(base)) return override.map((item) => cloneMergeItem(item));
    return override.map((item, index) => mergeArrayItem(base[index], item));
  }

  if (typeof override !== 'object' || typeof base !== 'object') {
    return override;
  }

  const result = { ...base };
  for (const key of Object.keys(override)) {
    const baseVal = base[key];
    const overrideVal = override[key];

    if (overrideVal === null || overrideVal === undefined) continue;

    if (Array.isArray(overrideVal)) {
      const baseArr = Array.isArray(baseVal) ? baseVal : [];
      result[key] = overrideVal.map((item, index) => mergeArrayItem(baseArr[index], item));
    } else if (
      typeof overrideVal === 'object' &&
      typeof baseVal === 'object' &&
      baseVal !== null &&
      !Array.isArray(baseVal)
    ) {
      result[key] = deepMerge(baseVal, overrideVal);
    } else {
      result[key] = overrideVal;
    }
  }
  return result;
}

function cloneMergeItem(item) {
  if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
    return { ...item };
  }
  return item;
}

function mergeArrayItem(baseItem, overrideItem) {
  if (overrideItem === null || overrideItem === undefined) return baseItem;
  if (baseItem === null || baseItem === undefined) return cloneMergeItem(overrideItem);

  if (
    typeof overrideItem === 'object' &&
    !Array.isArray(overrideItem) &&
    typeof baseItem === 'object' &&
    !Array.isArray(baseItem)
  ) {
    return deepMerge(baseItem, overrideItem);
  }

  return overrideItem;
}

const ROUTE_FALLBACK = {
  fromCity: '',
  fromCode: '—',
  toCity: '',
  toCode: '—',
  duration: '—',
};

function sanitizeRoutes(routes, canonical) {
  if (!Array.isArray(routes)) return [];
  return routes.map((route, index) => {
    if (!route || typeof route !== 'object') return { ...ROUTE_FALLBACK };
    const canon = canonical?.[index];
    return {
      ...ROUTE_FALLBACK,
      ...route,
      fromCode: pick(route.fromCode, canon?.fromCode, ROUTE_FALLBACK.fromCode),
      toCode: pick(route.toCode, canon?.toCode, ROUTE_FALLBACK.toCode),
      duration: pick(route.duration, canon?.duration, ROUTE_FALLBACK.duration),
    };
  });
}

function pick(...values) {
  for (const value of values) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) return trimmed;
  }
  return '—';
}

function sanitizeFaqItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      question: String(item.question ?? '').trim(),
      answer: String(item.answer ?? '').trim(),
    }))
    .filter((item) => item.question || item.answer);
}

/** Fill gaps in merged content so partial admin edits never crash the public site. */
export function sanitizeContent(content, canonicalRoutes) {
  if (!content || typeof content !== 'object') return content;

  const next = { ...content };

  if (next.activeRoutes?.routes) {
    next.activeRoutes = {
      ...next.activeRoutes,
      routes: sanitizeRoutes(next.activeRoutes.routes, canonicalRoutes),
    };
  }

  if (next.faq?.items) {
    next.faq = {
      ...next.faq,
      id: next.faq.id || 'faq',
      title: next.faq.title || '',
      items: sanitizeFaqItems(next.faq.items),
    };
  }

  return next;
}
